import { DATASETS_ROUTE } from "@/features/datasets/routes";
import { cn } from "@/utils/cn";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { deleteFastDataset, fastDatasetsKey, useFastDatasets } from "../api";
import { FadeText } from "./Fade";

export const FAST_ROUTE = DATASETS_ROUTE;

/** Past datasets in the left panel; the open one is highlighted. */
export const FastDatasetList = ({ onNavigate }: { onNavigate?: () => void }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const active = params.get("d");
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useFastDatasets();

  const remove = async (id: string, question: string) => {
    if (!window.confirm(`Delete “${question.slice(0, 80)}”?`)) return;
    try {
      await deleteFastDataset(id);
      if (active === id) navigate(FAST_ROUTE);
      void queryClient.invalidateQueries({ queryKey: fastDatasetsKey });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not delete the dataset.");
    }
  };

  if (isLoading) return <p className="font-ui px-5 py-2 text-xs text-zinc-400">Loading…</p>;
  if (error) return <p className="font-ui px-5 py-2 text-xs text-red-700">Could not load datasets.</p>;
  if (!data?.length) return <p className="font-ui px-5 py-2 text-xs text-zinc-400">No datasets yet</p>;

  return (
    <ul className="h-full overflow-y-auto pb-2" aria-label="Datasets">
      {data.map((d) => (
        <li key={d.id} className="group relative">
          <button
            type="button"
            aria-current={active === d.id ? "page" : undefined}
            onClick={() => {
              navigate(`${FAST_ROUTE}?d=${d.id}`);
              onNavigate?.();
            }}
            title={d.question}
            className={cn(
              "flex w-full items-center gap-2 py-1.5 pl-5 pr-8 text-left text-[13px] text-zinc-500 transition-colors hover:text-zinc-900",
              active === d.id && "text-zinc-900",
            )}
          >
            {d.status === "running" ? (
              <Loader2 className="h-3 w-3 shrink-0 animate-spin text-zinc-400" aria-label="Building" />
            ) : (
              <span
                className={cn(
                  "h-1.5 w-1.5 shrink-0 rounded-full",
                  d.status === "failed" ? "bg-red-500" : d.status === "review" ? "border border-zinc-400" : "bg-emerald-600",
                )}
                aria-label={d.status === "failed" ? "Failed" : d.status === "review" ? "Columns to review" : "Done"}
              />
            )}
            <FadeText className="flex-1 text-left">{d.question}</FadeText>
            {d.settings?.auto_approved ? (
              <span className="font-ui text-2xs text-zinc-300" title="Columns auto-approved">auto</span>
            ) : null}
            {d.row_count ? <span className="font-ui text-2xs tabular-nums text-zinc-300">{d.row_count}</span> : null}
          </button>
          <button
            type="button"
            onClick={() => void remove(d.id, d.question)}
            className="absolute right-3 top-1/2 hidden -translate-y-1/2 text-zinc-300 hover:text-zinc-700 group-hover:block"
            aria-label={`Delete ${d.question}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  );
};
